import { useCallback, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { useEnableTfa } from '@/modules/profile-settings/hooks/useEnableTfa.ts'
import { useTfaQRCode } from '@/modules/profile-settings/hooks/useTfaQRCode.ts'
import { useGenerateRecoveryCodes } from '@/modules/profile-settings/hooks/useTfaRecoveryCodes.ts'
import { BackupCodesDialog } from '@/modules/profile-settings/ui/tfa/backup-codes-dialog.tsx'

interface Enable2faPopupProps {
  show: boolean
  onClose: () => void
}

export type EnableTfaForm = z.infer<typeof enableTfaFormValues>

const enableTfaFormValues = z.object({
  authCode: z.string(),
})

export const EnableTfaDialog = ({ show, onClose }: Enable2faPopupProps) => {
  const { data: qrCode, isFetching } = useTfaQRCode()
  const generateRecoveryCodes = useGenerateRecoveryCodes()
  const enableTfaMutation = useEnableTfa()
  const form = useForm<EnableTfaForm>({
    mode: 'all',
    resolver: zodResolver(enableTfaFormValues),
    defaultValues: {
      authCode: '',
    },
  })
  const { setTfaEnabled } = useAccount()
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [showBackupCodes, setShowBackupCodes] = useState(false)
  const toast = useToast()

  const { handleSubmit } = form

  const onSubmit = useCallback(
    (values: EnableTfaForm) => {
      enableTfaMutation
        .mutateAsync(values.authCode)
        .then(async () => {
          const recoveryCodes = await generateRecoveryCodes.mutateAsync()

          setBackupCodes(recoveryCodes)
          setShowBackupCodes(true)
          setTfaEnabled(true)
          toast.success('Two-factor authentication enabled successfully.')
        })
        .catch((error) => {
          const errorMessage = error?.response?.data?.detail

          if (!errorMessage) return

          form.setError('authCode', {
            type: 'manual',
            message: errorMessage,
          })
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableTfaMutation],
  )

  const handleCancel = useCallback(() => {
    form.reset({ authCode: '' })
    form.clearErrors(['authCode'])
  }, [form])

  const handleClose = useCallback(() => {
    onClose()
    handleCancel()
    setShowBackupCodes(false)
  }, [onClose, handleCancel, setShowBackupCodes])

  if (showBackupCodes) {
    return (
      <BackupCodesDialog
        show={showBackupCodes}
        onClose={handleClose}
        backupCodes={backupCodes}
      />
    )
  }

  return (
    <FormProvider {...form}>
      <Dialog
        show={show}
        onClose={onClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Header>
            <Dialog.Title>Activate Two-Factor Authentication</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <p className="mt-4 text-center">
              Use your mobile device to scan the QR code with a two-factor
              authentication app like Google Authenticator to start generating
              authenticator codes.
            </p>
          </Dialog.Description>
          <div className="mt-2 flex flex-col gap-4">
            {isFetching || !qrCode ? (
              <div className="mx-auto">
                <Skeleton className="aspect-square h-64" />
              </div>
            ) : (
              <img
                src={qrCode}
                alt="QR Code"
                className=" mx-auto aspect-square h-64 "
              />
            )}
            <Separator />
            <FormField
              name="authCode"
              label="Authenticator Code"
              placeholder="Enter code"
            />
          </div>
          <Dialog.ClosableFooter onClose={handleClose}>
            <Button type="submit">Activate</Button>
          </Dialog.ClosableFooter>
        </form>
      </Dialog>
    </FormProvider>
  )
}
